import { useState } from "react";
import {
  Form,
  Input,
  Select,
  Upload,
  Image,
  Popconfirm,
  Button,
  Switch,
  Row,
  DatePicker,
  Divider,
  Space,
} from "antd";
import { FirebaseService, ToastService } from "@shared/lib/services";
import { ACCOUNT_STATUS, BrandType, IBrand } from "@shared/lib/types";
import dayjs from "dayjs";

import styles from "./styles.module.scss";
import TextArea from "antd/es/input/TextArea";
import { PlusOutlined } from "@ant-design/icons";

const { Option } = Select;

interface BrandFormProps {
  onSubmit: (values: Partial<IBrand>) => void;
  isSubmitting: boolean;
  initialValues: Pick<
    IBrand,
    "aggreementExpireOn" | "aggreementNotes" | "paymentTerms" | "aggreementUrl"
  >;
  paymentTerms: string[];
}

export function AggrementUpdateForm({
  onSubmit,
  isSubmitting,
  initialValues,
  paymentTerms,
}: BrandFormProps) {
  const [form] = Form.useForm();
  const [availablePaymentTerms, setAvailablePaymentTerms] =
    useState(paymentTerms);
  const [paymentTerm, setPaymentTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleSubmit = (values: any) => {
    onSubmit({
      ...values,
      aggreementExpireOn: values.aggreementExpireOn
        ? dayjs(values.aggreementExpireOn).toISOString()
        : undefined,
    });
  };
  const addNewPaymentTerm = (roleName: string) => {
    if (!roleName.trim() || availablePaymentTerms.includes(roleName)) {
      return;
    }

    const updatedRoles = [...availablePaymentTerms, roleName];
    setAvailablePaymentTerms(updatedRoles);
    setPaymentTerm("");
    form.setFieldsValue({ paymentTerms: roleName });
    setDropdownOpen(false);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      className={styles.form}
      initialValues={{
        aggreementExpireOn: initialValues.aggreementExpireOn
          ? dayjs(initialValues.aggreementExpireOn)
          : null,
        aggreementNotes: initialValues.aggreementNotes,
        aggreementUrl: initialValues.aggreementUrl,
        paymentTerms: initialValues.paymentTerms,
      }}
      onFinishFailed={({ errorFields }) => {
        if (errorFields.length) {
          ToastService.error(
            "Error Submitting Form. Please fill all required fields."
          );
        }
      }}
    >
      <div className={styles.content}>
        <Form.Item
          label="Aggrement Link"
          name="aggreementUrl"
          rules={[
            { required: false },
            { type: "url", message: "Please enter a valid URL" },
          ]}
        >
          <Input placeholder="Url" />
        </Form.Item>
        <Form.Item name="aggreementNotes" label="Aggrement Notes">
          <TextArea placeholder="Aggrement Notes" />
        </Form.Item>
        <Form.Item name="aggreementExpireOn" label="Agreement Expiry Date">
          <DatePicker
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
            allowClear={false}
            onChange={(date) => {
              form.setFieldsValue({
                aggreementExpireOn: date ? date : undefined,
              });
            }}
          />
        </Form.Item>
        <Form.Item name="paymentTerms" label="Payment Terms">
          <Input placeholder="Payment Terms" />
        </Form.Item>

        <Form.Item
          name="paymentTerms"
          label="Payment Terms"
          rules={[{ required: true, message: "Payment Term is required" }]}
        >
          <Select
            open={dropdownOpen}
            onDropdownVisibleChange={setDropdownOpen}
            showSearch
            placeholder="Select or create a paymentTerm"
            optionFilterProp="children"
            allowClear
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: "8px 0" }} />
                <Space
                  style={{
                    padding: "0 8px 4px",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Input
                    placeholder="Enter new role"
                    value={paymentTerm}
                    onChange={(e) => setPaymentTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && paymentTerm) {
                        e.stopPropagation();

                        e.preventDefault();
                        addNewPaymentTerm(paymentTerm);
                      }
                    }}
                  />
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    disabled={!paymentTerm}
                    onClick={() => addNewPaymentTerm(paymentTerm)}
                  >
                    Add Role
                  </Button>
                </Space>
              </>
            )}
            filterOption={(input, option) =>
              (option?.children as unknown as string)
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {availablePaymentTerms.map((role) => (
              <Option key={role} value={role}>
                {role}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </div>

      <Form.Item>
        <Row justify={"end"}>
          {" "}
          <Button
            type="primary"
            htmlType="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Row>
      </Form.Item>
    </Form>
  );
}
